<template>
    <DForm @submit.prevent="submitForm">
        <DCardBody>
            <DButton
                icon="map-legend"
                :label="__('settings.invoice_pdf_settings.legend')"
                class="mb-3"
                @click="showLegend = !showLegend"
            />
            <DCollapse v-show="showLegend">
                <DTable
                    v-show="showLegend"
                    :items="placeholders"
                    :fields="[
                        {key: 'placeholder', label: __('settings.invoice_pdf_settings.available_placeholders')},
                        {key: 'description', label: __('settings.invoice_pdf_settings.description')},
                    ]"
                />
            </DCollapse>
            <br>
            <DButton
                icon="cog-outline"
                :label="__('tools.emails.placeholder_groups.help_on_template_logic')"
                class="mb-3"
                @click="showHelpOnTemplateLogic = !showHelpOnTemplateLogic"
            />
            <DCollapse v-show="showHelpOnTemplateLogic">
                <HelpTemplateDocs :documentations="documentation" />
            </DCollapse>
            <DFormRow
                :label="__('customers.customers.brand') "
                :error="errors['invoice_pdf_setting_brand_id']"
                is-vertical
                is-required
                :hint="__('customers.customers.brand_hint')"
            >
                <DSelect
                    id="brand"
                    v-model="form.invoice_pdf_setting_brand_id"
                    :options="brands"
                    :placeholder="__('customers.customers.brand')"
                    :disabled="editPage === true"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.invoice_pdf_settings.form.language')"
                :error="errors['language_code']"
                is-vertical
                is-required
                :hint="__('settings.invoice_pdf_settings.form.language_hint')"
            >
                <DSelect
                    v-model="form.language_code"
                    :options="allLanguages"
                    :disabled="editPage === true"
                />
            </DFormRow>
            <div
                v-for="(configuration, index) in configurations"
                :key="index"
            >
                <DRow class="mb-3">
                    <DCol col="12">
                        <h2 class="h5">
                            {{ __(`settings.invoice_pdf_settings.payment_methods.${configuration.payment_method}`) }}
                            <label
                                v-if="isValid(index, 'payment_method') === false"
                                class="text-danger"
                            >
                                <small>{{ invalidFeedback(index, 'payment_method') }}</small>
                            </label>
                        </h2>
                    </DCol>
                    <DCol col="12">
                        <DFormTextarea
                            :id="`invoice_text_${index}`"
                            v-model="form.invoicePdfSettingsData[index].invoice_text"
                            rows="5"
                            :label="__('settings.invoice_pdf_settings.form.invoice_text')"
                            :is-valid="isValid(index, 'invoice_text')"
                            :invalid-feedback="invalidFeedback(index, 'invoice_text')"
                        />
                    </DCol>
                </DRow>
                <DRow
                    v-if="configuration.payment_method === 'credit_card'"
                    class="mb-3"
                >
                    <DCol col="12">
                        <h2 class="h5">
                            <label
                                v-if="isValid(index, 'second_invoice_text') === false"
                                class="text-danger"
                            >
                                <small>{{ invalidFeedback(index, 'second_invoice_text') }}</small>
                            </label>
                        </h2>
                    </DCol>
                    <DCol col="12">
                        <DFormTextarea
                            :id="`second_invoice_text_${index}`"
                            v-model="form.invoicePdfSettingsData[index].second_invoice_text"
                            rows="5"
                            :label="__('settings.invoice_pdf_settings.form.second_invoice_text')"
                            :is-valid="isValid(index, 'second_invoice_text')"
                            :invalid-feedback="invalidFeedback(index, 'second_invoice_text')"
                        />
                    </DCol>
                </DRow>
                <DRow
                    v-if="configuration.payment_method === 'credit_card'"
                    class="mb-3"
                >
                    <DCol col="12">
                        <h2 class="h5">
                            <label
                                v-if="isValid(index, 'third_invoice_text') === false"
                                class="text-danger"
                            >
                                <small>{{ invalidFeedback(index, 'third_invoice_text') }}</small>
                            </label>
                        </h2>
                    </DCol>
                    <DCol col="12">
                        <DFormTextarea
                            :id="`third_invoice_text_${index}`"
                            v-model="form.invoicePdfSettingsData[index].third_invoice_text"
                            rows="5"
                            :label="__('settings.invoice_pdf_settings.form.third_invoice_text')"
                            :is-valid="isValid(index, 'third_invoice_text')"
                            :invalid-feedback="invalidFeedback(index, 'third_invoice_text')"
                        />
                    </DCol>
                </DRow>
                <DRow
                    v-if="configuration.payment_method === 'credit_card'"
                    class="mb-3"
                >
                    <DCol col="12">
                        <h2 class="h5">
                            <label
                                v-if="isValid(index, 'fourth_invoice_text') === false"
                                class="text-danger"
                            >
                                <small>{{ invalidFeedback(index, 'fourth_invoice_text') }}</small>
                            </label>
                        </h2>
                    </DCol>
                    <DCol col="12">
                        <DFormTextarea
                            :id="`fourth_invoice_text_${index}`"
                            v-model="form.invoicePdfSettingsData[index].fourth_invoice_text"
                            rows="5"
                            :label="__('settings.invoice_pdf_settings.form.fourth_invoice_text')"
                            :is-valid="isValid(index, 'fourth_invoice_text')"
                            :invalid-feedback="invalidFeedback(index, 'fourth_invoice_text')"
                        />
                    </DCol>
                </DRow>
            </div>
            <DRow>
                <DCol col="12">
                    <h2 class="h5">
                        {{ __('settings.invoice_pdf_settings.form.any_payment_method') }}
                    </h2>
                </DCol>
                <DCol col="12">
                    <DFormTextarea
                        id="invoice_cancellation_text"
                        v-model="form.invoice_cancellation_text"
                        rows="5"
                        :label="__('settings.invoice_pdf_settings.form.invoice_cancellation_text')"
                        :is-valid="errors.invoice_cancellation_text !== undefined ? false : null"
                        :invalid-feedback="errors.invoice_cancellation_text"
                    />
                </DCol>
                <DCol col="12">
                    <DFormTextarea
                        id="prepaid_invoice_text"
                        v-model="form.prepaid_text"
                        rows="5"
                        :label="__('settings.invoice_pdf_settings.form.prepaid_text')"
                        :is-valid="errors.prepaid_text !== undefined ? false : null"
                        :invalid-feedback="errors.prepaid_text"
                    />
                </DCol>
            </DRow>
            <DFormControls
                :dirty="form.isDirty"
                @submit="submitForm"
            />
        </DCardBody>
    </DForm>
</template>

<script>

import HelpTemplateDocs from '~@/Components/Common/Settings/HelpTemplateDocs.vue'
import documentDocs from '~@/Mixins/documentDocs'

export default {
    name: 'InvoicePdfSettingForm',
    components: { HelpTemplateDocs },
    mixins: [documentDocs],
    props: {
        editPage: {
            type: Boolean,
            default: () => { return true }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        configurations: {
            type: Array,
            default: () => { return [] }
        },
        placeholders: {
            type: Array,
            default: () => { return [] }
        },
        brands: {
            type: Array,
            default: () => { return [] }
        },
        allLanguages: {
            type: Array,
            default: () => { return [] }
        }
    },
    emits: ['InvoicePdfSettingFormSubmit'],
    data () {
        return {
            form: null,
            invoicePdfSettingsData: [],
            showLegend: false,
            showHelpOnTemplateLogic: false
        }
    },
    computed: {
        documentation () {
            return documentDocs
        }
    },
    created () {
        this.configurations.forEach((configuration) => {
            this.invoicePdfSettingsData.push({
                invoice_pdf_setting_id: configuration.id,
                payment_method: configuration.payment_method,
                invoice_text: configuration.invoice_text,
                second_invoice_text: configuration.second_invoice_text,
                third_invoice_text: configuration.third_invoice_text,
                fourth_invoice_text: configuration.fourth_invoice_text
            })
        })
        this.form = this.$inertia.form({
            invoice_pdf_setting_brand_id: this.configurations[0]?.brand_id ?? '',
            invoicePdfSettingsData: this.invoicePdfSettingsData,
            invoice_cancellation_text: this.configurations[0]?.invoice_cancellation_text,
            prepaid_text: this.configurations[0]?.prepaid_text,
            language_code: this.configurations[0]?.language_code ?? null,
            second_invoice_text: this.configurations[0]?.second_invoice_text ?? null,
            third_invoice_text: this.configurations[0]?.third_invoice_text ?? null,
            fourth_invoice_text: this.configurations[0]?.fourth_invoice_text ?? null
        })
    },
    methods: {
        submitForm () {
            this.$emit('InvoicePdfSettingFormSubmit', this.form)
        },
        isValid (index, field) {
            if (typeof this.errors[`invoicePdfSettingsData.${index}.${field}`] !== 'undefined') {
                return false
            }

            return null
        },
        invalidFeedback (index, field) {
            if (this.isValid(index, field) === false) {
                return this.errors[`invoicePdfSettingsData.${index}.${field}`]
            }

            return null
        }
    }
}
</script>
